<template>
  <article
    class="overflow-hidden relative rounded-2xl shadow-lg transition-shadow duration-300 ease-in-out hover:shadow-2xl"
    :class="sizeClasses.container"
  >
    <!-- Imagem normal ou placeholder delicado -->
    <template v-if="!isFallback">
      <div v-if="!loaded" class="absolute inset-0 skeleton" aria-hidden="true"></div>
      <img
        :src="post.image"
        :alt="post.alt"
        class="object-cover absolute inset-0 w-full h-full transition-opacity duration-500 select-none"
        :class="loaded ? 'opacity-100' : 'opacity-0'"
        :loading="eager ? 'eager' : 'lazy'"
        decoding="async"
        :fetchpriority="eager ? 'high' : 'low'"
        :sizes="sizeClasses.imgSizes"
        @load="loaded = true"
        aria-hidden="true"
      />
      <div
        class="absolute inset-0 bg-gradient-to-t to-transparent pointer-events-none from-black/70 via-black/40"
        aria-hidden="true"
      ></div>
    </template>
    <template v-else>
      <div class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-[#F3FAFD] to-[#E6F3F8]">
        <img :src="placeholderPicture" alt="" :class="sizeClasses.placeholderIcon" aria-hidden="true" />
        <span class="mt-2 text-[#1b6b82] font-medium" :class="sizeClasses.placeholderText">Sem imagem</span>
      </div>
    </template>
    <div class="absolute top-3 left-3">
      <span
        class="font-semibold uppercase tracking-widest rounded-md shadow-md"
        :class="sizeClasses.tag"
        >{{ post.tag }}</span
      >
    </div>
    <div class="flex absolute inset-0 items-end p-4">
      <component
        :is="size === 'big' ? 'h3' : 'h4'"
        :class="[isFallback ? 'text-[#004d6b]' : 'text-white', sizeClasses.title]"
      >
        {{ post.title }}
      </component>
    </div>
    <!-- Overlay de navegação acessível cobrindo todo o card -->
    <RouterLink
      class="absolute inset-0 z-10 rounded-2xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kali-blue-600 focus:ring-offset-transparent active:opacity-90"
      :to="linkTo"
      :aria-label="'Abrir postagem: ' + (post.title || '')"
    >
      <span class="sr-only">Abrir postagem</span>
    </RouterLink>
  </article>
</template>

<script setup>
import { ref, computed } from "vue";
import { RouterLink } from "vue-router";
import placeholderPicture from "@/assets/picture-placeholder.svg";

const props = defineProps({
  post: { type: Object, required: true },
  // "big": card principal (1 ou 2 postagens) | "small": card lateral empilhado (3+ postagens)
  size: { type: String, default: "small" },
  eager: { type: Boolean, default: false },
});

const loaded = ref(false);

const isFallback = computed(() => {
  const p = props.post;
  if (!p) return true;
  const img = p.image || "";
  const used = Boolean(p?.imageConsistency?.usedFallback);
  return used || img === placeholderPicture;
});

const sizeClasses = computed(() =>
  props.size === "big"
    ? {
        container: "h-[324px] sm:h-[384px] lg:h-[384px]",
        imgSizes: "(min-width:1024px) 66vw, 100vw",
        tag: "px-3 py-1 text-xs bg-[#006086] text-white",
        title: "text-xl sm:text-2xl font-semibold leading-snug text-shadow-post",
        placeholderIcon: "w-14 h-14 opacity-70",
        placeholderText: "text-sm",
      }
    : {
        container: "h-[150px] sm:h-[180px]",
        imgSizes: "(min-width:1024px) 33vw, 100vw",
        tag: "px-3 py-1 text-[10px] bg-white/90 text-[#004d6b]",
        title: "text-base sm:text-lg font-semibold leading-snug text-shadow-post-sm",
        placeholderIcon: "w-10 h-10 opacity-70",
        placeholderText: "text-xs",
      },
);

const linkTo = computed(() =>
  props.post && props.post.slug
    ? { name: "blog-detail", params: { slug: props.post.slug } }
    : { name: "blog" },
);
</script>

<style scoped>
.text-shadow-post {
  text-shadow:
    0 2px 10px rgba(0, 0, 0, 0.65),
    0 1px 2px rgba(0, 0, 0, 0.55);
}

.text-shadow-post-sm {
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
}

.skeleton {
  background: linear-gradient(90deg, #f3f4f6, #e5e7eb, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: 200% 0;
  }
}
</style>
